import React, {Fragment} from 'react'
import {H6, Row, Button, Modal, ModalHeader, ModalActions} from '../../UI/index.js'
import CarrierSelector from '../../parcels/CarrierSelector.js'
import {formSpacing, CurrencyInput, FormRow, FormStack, Input, Select, Toggle} from '../../UI/forms/index.js'
import _ from 'lodash'
import Countries from '../../../utils/Countries.js'
import type {Reseller} from '../../../../server/functions/resellers/resellers.types.js'
import generateUUID from '../../../utils/generateUUID.js'
import type {ParcelCarrier} from '../../../../server/functions/parcels/carrier.types.js'
import {formatPrice} from '../../../../utils/price.js'
import t from '../../../helpers/Languages.js'
import ParcelConditionGroupsEditor from '../../conditions/ParcelConditionGroupsEditor.js'

type ParcelFee = Reseller['settings']['parcels']['fees'][number]

interface ParcelFeeModalProps {
    reseller: Reseller
    carriers: ParcelCarrier[]
    onChange: (fee: ParcelFee, index: number | null) => void
    onRemove: (index: number) => void
}

interface ParcelFeeModalState {
    open: boolean
    fee: ParcelFee
    index: number | null
}

const newFee = (): ParcelFee => {
    return {
        id: generateUUID(),
        description: '',
        basedOn: 'fixedPrice',
        price: '',
        productCode: '',
        auto: false,
        applyToAllCarriers: false,
        carriers: [],
        applyToAllCountries: false,
        countries: [],
        postalCodes: {},
        conditionGroups: [],
        matchAllConditions: false
    }
}

// Countries for which the fee can be limited to a postal code area, Nederland first
const postalCodeCountries = _.sortBy(
    Countries.filter((country) => country.code2 === 'NL' || ['EU1', 'EU2'].includes(country.category)),
    (country) => country.code2 !== 'NL'
)

export default class ParcelFeeModal extends React.Component<ParcelFeeModalProps, ParcelFeeModalState> {
    initialState: ParcelFeeModalState

    constructor(props: ParcelFeeModalProps) {
        super(props)

        this.initialState = {
            open: false,
            fee: newFee(),
            index: null
        }

        this.state = structuredClone(this.initialState)
    }

    open(fee?: ParcelFee | null, index?: number | null) {
        if (!fee) {
            fee = newFee()
        }

        fee.carriers = fee.carriers || []
        fee.countries = fee.countries || []
        fee.postalCodes = fee.postalCodes || {}
        fee.conditionGroups = fee.conditionGroups || []

        fee = structuredClone(fee)

        this.setState({open: true, fee, index: index ?? null})
    }

    close() {
        this.setState(this.initialState)
    }

    onChangeValue(path: string, parser: ((value: string) => string | number) | React.ChangeEvent<HTMLInputElement>, event?: React.ChangeEvent<HTMLInputElement>) {
        const fee = {...this.state.fee}

        if (!event) {
            event = parser as React.ChangeEvent<HTMLInputElement>
        }

        if (typeof parser === 'function') {
            _.set(fee, path, parser(event.target.value))
        } else {
            _.set(fee, path, event.target.value)
        }

        this.setState({fee})
    }

    // Percentages are stored as strings (like prices) and may have up to 2 decimals
    onChangePercentage(event: React.ChangeEvent<HTMLInputElement>) {
        const value = event.target.value.replace('.', ',')

        if (!/^$|^[0-9]{0,3}$|^[0-9]{0,3},[0-9]{0,2}$/.test(value)) {
            return
        }

        this.setState({fee: {...this.state.fee, price: value}})
    }

    onBlurPercentage() {
        const {fee} = this.state

        if (!fee.price) {
            return
        }

        const percentage = Math.min(100, Math.max(0, parseFloat(fee.price.replace(',', '.')) || 0))

        this.setState({fee: {...fee, price: percentage.toFixed(2).replace('.', ',')}})
    }

    onChangeChecked(path: string, event: React.ChangeEvent<HTMLInputElement>) {
        const fee = {...this.state.fee}

        _.set(fee, path, event.target.checked)

        this.setState({fee})
    }

    onChangeCarriers(event: React.ChangeEvent<HTMLInputElement>) {
        const fee = {...this.state.fee}

        const index = fee.carriers.indexOf(event.target.value)

        if (index > -1) {
            fee.carriers.splice(index, 1)
        } else {
            fee.carriers.push(event.target.value)
        }

        this.setState({fee})
    }

    onChangeCountries(event: React.ChangeEvent<HTMLInputElement> & {target: {values: string[]}}) {
        const fee = {...this.state.fee}

        fee.countries = event.target.values

        this.setState({fee})
    }

    onSubmit() {
        const {fee, index} = this.state

        // Only keep filled in postal code areas of countries that are still selected
        fee.postalCodes = _.pickBy(fee.postalCodes, (postalCodes, country) => postalCodes && fee.countries.includes(country))

        if (fee.basedOn === 'fixedPrice') {
            fee.price = formatPrice(fee.price)
        }

        this.props.onChange(fee, index)

        this.close()
    }

    render() {
        const {open, fee, index} = this.state
        const {reseller, carriers, onRemove} = this.props

        const enabledCarriers = carriers.filter((carrier) => {
            return reseller.settings.parcels.carriers[carrier.name] && reseller.settings.parcels.carriers[carrier.name].enabled
        })

        return (
            <Modal style={{width: 800}} show={open} onClose={this.close.bind(this)}>
                <ModalHeader title='Wijzig toeslag' onClose={this.close.bind(this)} style={{marginBottom: 0}} />

                <FormStack style={{marginBottom: formSpacing.rowGap}}>
                    <Input
                        label='Beschrijving'
                        value={fee.description}
                        onChange={this.onChangeValue.bind(this, 'description')}
                    />

                    <Select
                        label='Toeslag op basis van'
                        value={fee.basedOn}
                        onChange={this.onChangeValue.bind(this, 'basedOn')}
                    >
                        <option value='fixedPrice'>Vaste prijs</option>
                        <option value='percentageOfProduct'>Percentage over product</option>
                        <option value='percentageOfProductPlusFixedPriceFees'>Percentage over product + vaste prijs toeslagen</option>
                    </Select>

                    {fee.basedOn === 'fixedPrice' ?
                        (
                            <CurrencyInput
                                label='Prijs'
                                value={fee.price}
                                onChange={this.onChangeValue.bind(this, 'price')}
                            />
                        ) :
                        (
                            <Input
                                label='Percentage'
                                value={fee.price}
                                onChange={this.onChangePercentage.bind(this)}
                                onBlur={this.onBlurPercentage.bind(this)}
                                inputMode='decimal'
                                append='%'
                            />
                        )}

                    <FormRow>
                        <Toggle
                            style={{marginTop: 24}}
                            label='Automatisch toepassen'
                            checked={fee.auto}
                            onChange={this.onChangeChecked.bind(this, 'auto')}
                        />

                        <div style={{flex: 1}} />
                    </FormRow>
                </FormStack>

                {fee.auto &&
                    <>
                        <H6 style={{marginBottom: 12, marginTop: 48}}>Vervoerders</H6>

                        <FormRow style={{marginBottom: formSpacing.rowGap}}>
                            <Toggle
                                style={{marginTop: 12}}
                                label='Toepassen op alle vervoerders'
                                checked={fee.applyToAllCarriers}
                                onChange={this.onChangeChecked.bind(this, 'applyToAllCarriers')}
                            />

                            <div style={{flex: 1}} />
                        </FormRow>

                        {!fee.applyToAllCarriers &&
                            <Row style={{marginTop: 12, width: '100%', overflowX: 'auto'}}>
                                <CarrierSelector
                                    label='Vervoerders'
                                    size={40}
                                    carriers={enabledCarriers}
                                    values={fee.carriers}
                                    onChange={this.onChangeCarriers.bind(this)}
                                />
                            </Row>
                        }

                        <H6 style={{marginBottom: 12, marginTop: 48}}>Landen</H6>

                        <FormStack style={{marginBottom: formSpacing.rowGap}}>
                            <FormRow>
                                <Toggle
                                    style={{marginTop: 12}}
                                    label='Toepassen op alle landen'
                                    checked={fee.applyToAllCountries}
                                    onChange={this.onChangeChecked.bind(this, 'applyToAllCountries')}
                                />

                                <div style={{flex: 1}} />
                            </FormRow>

                            {!fee.applyToAllCountries &&
                                <>
                                    <Select
                                        copy
                                        label='Landen'
                                        options={[
                                            ...Countries.map((country) => {
                                                return {value: country.code2, title: country.name}
                                            })
                                        ]}
                                        values={fee.countries || []}
                                        onChange={this.onChangeCountries.bind(this)}
                                    />

                                    {postalCodeCountries.filter((country) => fee.countries.includes(country.code2)).map((country) => (
                                        <Input
                                            key={country.code2}
                                            label={`Beperkt postcodegebied ${country.name}`}
                                            info={country.code2 === 'NL' ?
                                                'Dit zijn de postcodes waar de toeslag op wordt toegepast. Gebruik een komma om postcodes te scheiden en een streepje om een reeks te noteren. Postcodes kunnen mét of zónder letters gekozen worden. Bijvoorbeeld: 1000-2000, 3000, 4000AA-4000ZZ. Laat dit veld leeg om alle postcodes te gebruiken.' :
                                                'Dit zijn de postcodes waar de toeslag op wordt toegepast. Gebruik een komma om postcodes te scheiden en een streepje om een reeks te noteren. Alleen de cijfers van een postcode worden vergeleken. Bijvoorbeeld: 10000-19999, 20000. Laat dit veld leeg om alle postcodes te gebruiken.'
                                            }
                                            value={fee.postalCodes?.[country.code2] || ''}
                                            onChange={this.onChangeValue.bind(this, `postalCodes.${country.code2}`)}
                                        />
                                    ))}
                                </>
                            }
                        </FormStack>

                        <H6 style={{marginBottom: 12, marginTop: 48}}>{t('conditions')}</H6>

                        <ParcelConditionGroupsEditor
                            conditionGroups={fee.conditionGroups || []}
                            matchAll={!!fee.matchAllConditions}
                            reseller={this.props.reseller}
                            onChange={(conditionGroups, matchAllConditions) => this.setState({fee: {...this.state.fee, conditionGroups, matchAllConditions}})}
                        />
                    </>
                }

                <ModalActions
                    style={{marginTop: 36}}
                    start={typeof index === 'number' &&
                        <Button
                            variant='text'
                            onClick={() => {
                                onRemove(index)
                                this.close()
                            }}
                        >
                            Verwijder
                        </Button>
                    }
                >
                    <Button
                        variant='text'
                        onClick={() => {
                            this.close()
                        }}
                    >
                        Annuleren
                    </Button>

                    <Button disabled={!fee.description} onClick={this.onSubmit.bind(this)}>Opslaan</Button>
                </ModalActions>
            </Modal>
        )
    }
}
